const ECENTRIC_SCRIPT_ID = "ecentric-hpp-script";

let ecentricScriptPromise: Promise<void> | null = null;

/**
 * Lazily injects the ecentric Hosted Payment Page (HPP) script, which exposes
 * `window.hpp` for the lightbox payment flow.
 *
 * This used to be a global `<script>` in index.html, fetched on every page —
 * including the Binah face-scan route, which is cross-origin isolated
 * (`Cross-Origin-Embedder-Policy: require-corp`). COEP blocks the third-party
 * ecentric script there, producing console noise. Loading it on demand
 * keeps it off every other page and only fetches it where it's actually used.
 *
 * Idempotent: concurrent and repeated calls share a single load. A failed load
 * resets the cache so a later call can retry.
 */
export function loadEcentricScript(): Promise<void> {
  if (ecentricScriptPromise) {
    return ecentricScriptPromise;
  }

  const src = import.meta.env.VITE_APP_ECCENTRIC_SCRIPT_URL;
  if (!src) {
    // Don't cache a configuration failure — a later call should re-check.
    return Promise.reject(
      new Error("VITE_APP_ECCENTRIC_SCRIPT_URL is not configured")
    );
  }

  ecentricScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(ECENTRIC_SCRIPT_ID);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.id = ECENTRIC_SCRIPT_ID;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      ecentricScriptPromise = null;
      script.remove();
      reject(new Error(`Failed to load ecentric script: ${src}`));
    };
    document.head.appendChild(script);
  });

  return ecentricScriptPromise;
}
